import { HttpErrorResponse } from '@angular/common/http';

export class FieldFormatters {
    static maskPhoneNumber(phoneNumber: string) {
        if (phoneNumber) {
            return phoneNumber
                .replace(/(\d{2})?(\d{4,5})(\d{4})/, '($1) $2-$3')
                .replace('() ', '');
        }
        return null;
    }

    static maskCep(cep: string) {
        if (cep.length !== 8) return 'CEP inválido';
        return cep.replace(/(\d{5})(\d{3})/, '$1-$2');
    }

    static formatCnpj(cnpj: string) {
        return cnpj.replace(
            /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g,
            '$1.$2.$3/$4-$5'
        );
    }
}

export class DateFormatters {
    static str2ptbrDate = function (datestring: string) {
        if (!datestring) return 'Data não informada';
        const dateTest = new Date(datestring);
        if (isNaN(dateTest.getTime())) return 'Data inválida';

        const formattedDate = Intl.DateTimeFormat('pt-br', {
            timeZone: 'UTC',
        }).format(dateTest);

        const dateStringList = datestring.split('-'); // yyyy-MM-dd
        const formattedDateList = formattedDate.split('/'); // dd/MM/yyyy

        const validatorSet = [
            // ...Array.from workaround https://stackoverflow.com/questions/33464504/using-spread-syntax-and-new-set-with-typescript
            ...Array.from(new Set([...dateStringList, ...formattedDateList])),
        ];

        if (validatorSet.length > 3) return 'Data inválida';
        return formattedDate;
    };

    static getFormattedDateOrEmptyString(
        valor: string,
        tipo: 'ISO' | 'ABNT'
    ): string {
        try {
            switch (tipo) {
                case 'ABNT':
                    return DateFormatters.formatDateToABNTString(valor);
                case 'ISO':
                    return DateFormatters.formatDateABNTToISOString(valor);
            }
        } catch (erro) {
            return '';
        }
    }

    /**
     * Convert date string to Date object
     * @param d Date String "yyyy-MM-dd"
     */
    static formatDateToABNTString(d: string) {
        const pattern = /(\d{4})-(\d{1,2})-(\d{1,2})/;
        if (!pattern.test(d)) throw Error('Invalid date format');

        const [year, month, day] = d.split('-');

        return [day.padStart(2, '0'), month.padStart(2, '0'), year].join('/');
    }

    static formatDateABNTToISOString(d: string) {
        const pattern = /(\d{1,2})\/(\d{1,2})\/(\d{4})/;
        if (!pattern.test(d)) throw Error('Invalid date format');

        const [day, month, year] = d.split('/');

        return [year, parseInt(month), parseInt(day)].join('-');
    }

    /**
     * Convert date string to Date object
     * @param date Date String "yyyy-MM-dd"
     * @returns new Date object
     */
    static formatDate(date: string | Date) {
        if (typeof date === 'string') {
            const [year, month, day] = date.split('-');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }

        throw Error('Date must be a string');
    }

    /**
     * Parse "mm-aaaa" to "yyyy-mm-dd" on day 01
     * @param date string formated "mm/aaaa"
     */
    static monthToISO(date: string) {
        if (date) {
            const [month, year] = date.split('-');
            return [year, month, '01'].join('-');
        }
        return null;
    }

    /**
     * Parse "mm-aaaa" to "yyyy-mm-dd" on last day
     * @param date string formated "mm-aaaa"
     */
    static monthToISOAlternative(date: string) {
        if (date) {
            const [month, year] = date.split('-');
            const lastDay = new Date(
                parseInt(year),
                parseInt(month),
                0
            ).getDate();
            return [year, month, lastDay].join('-');
        }
        return null;
    }

    /**
     * Parse "yyy-mm-dd" on any day to "mm/aaaa"
     * @param date string formated "yyyy-mm-dd"
     */
    static parseMonth(date: string) {
        if (date) {
            const [year, month, day] = date.split('-');
            return [month, year].join('/');
        }
        return null;
    }

    /***
     * Leva em consideração os diferentes formatos de data que podem ser recebidos.
     *
     ***/
    static sumeDateFormatter(date: string): Date {
        if (typeof date === 'string') {
            let formattedDate: Date;

            if (date.includes('-')) {
                const [day, month, year] = date.split('-');
                formattedDate = new Date(
                    parseInt(day),
                    parseInt(month) - 1,
                    parseInt(year)
                );
            } else {
                const [year, month, day] = date.split('/');
                formattedDate = new Date(
                    parseInt(day),
                    parseInt(month) - 1,
                    parseInt(year)
                );
            }
            return formattedDate;
        }
        throw Error('Data inválida');
    }

    static monthDateFormatter(date: string): Date {
        if (typeof date === 'string') {
            let formattedDate: Date;

            if (date.includes('-')) {
                const [month, year] = date.split('-');
                formattedDate = new Date(parseInt(year), parseInt(month) - 1);
            } else {
                const [year, month, day] = date.split('/');
                formattedDate = new Date(parseInt(year), parseInt(month) - 1);
            }
            return formattedDate;
        }
        throw Error('Data inválida');
    }
}

export class NumberFormatters {
    /**
     * float to pt-BR
     */
    static f2ptbr = new Intl.NumberFormat('pt-BR', {
        maximumFractionDigits: 3,
        minimumFractionDigits: 3,
    });

    /**
     * float to pt-BR
     */
    static f2ptbr2 = new Intl.NumberFormat('pt-BR', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    });

    /**
     * BRL Currency
     */
    static formatter = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });

    /**
     * Converts BRL string to float number
     * @param num BRL string
     * @returns float number
     */
    static toFloat = (num: string) => {
        if (num == null) return 0;
        return parseFloat(num.replace('.', '').replace(',', '.'));
    };
}

export class ErrorFormatters {
    static formattedError = function (err: HttpErrorResponse | any) {
        function formatDRFSerialized(error: { [key: string]: string[] }) {
            return Object.entries(error).map(el => {
                if (el[1].join) return ` "${el[0]}": ${el[1]?.join(', ')}`;
                else return ` "${el[0]}": ${el[1]}`;
            });
        }
        let textMsg;
        if (err.error) {
            if (typeof err.error === 'string') {
                const MAX_TEXT_LENGTH = 100;

                textMsg = err.error.slice(0, MAX_TEXT_LENGTH);
                if (err.error.length > MAX_TEXT_LENGTH) textMsg += '...';
            } else
                textMsg =
                    err['error'].non_field_errors ||
                    err['error'].message ||
                    err['error'].detail ||
                    formatDRFSerialized(err.error) ||
                    'Unhandled error: ' +
                        JSON.stringify(err['error']).slice(0, 200) +
                        (err['error'].length > 200 ? '...' : '');
        } else {
            textMsg = err['message'] || err['name'] || JSON.stringify(err);
            if (err['statusText']) textMsg += '\n' + err['statusText'];
        }
        switch (err['status']) {
            case 0:
                textMsg +=
                    '\nNão foi possível acessar o servidor de autenticação';
        }

        return textMsg;
    };
}
